import React, { Component, useEffect, useMemo, useCallback, useState } from 'react';
import { useGetViewsQuery, useCreateViewLabelMutation } from '../../redux/slices/views';
import { ViewLabel, ViewLabelFramesPage } from 'models/view-labels';
import { Col, Empty, Pagination, Row, Typography, message } from 'antd';
import {
  ViewLabelsWrapper,
  StatsBarWrapper,
  StatsContentWrapper,
  PaginationWrapper,
} from './styled';
import Skeleton from '../Skeleton/Skeleton';
import { useCreateReviewMutation } from '../../redux/slices/reviews';
import {
  useGetViewLabelFramesQuery,
  useLazyGetViewLabelFramesQuery,
  useGetViewLabelStatsQuery,
} from '../../redux/slices/keypoints';
import { AdvancedFiltersOptions, AdvancedFilterValue } from 'models/advancedFilters';

import { useViewLabelFilterState } from '../../hooks/useFilterState';
import { useSearchParams } from 'react-router-dom';
import { Decision } from '../../models/reviews';
import { View } from 'models/views';
import { ViewLabelItem, RejectModal } from './components';
import ViewLabelFilter from 'components/Filters/ViewLabelFilter';
import { DicomGroup } from 'models/view-labels';

interface ViewLabelsProps {
  filterState: any;
  onLocalDecision: (uuid: string, decision: Decision) => void;
  getLocalDecision: (uuid: string) => Decision | undefined;
  viewLabelsPage: ViewLabelFramesPage | undefined;
  isLoadingGetViewLabels: boolean;
  views: any;
  kpClasses: any;
  createReview: any;
  keypointStats: any;
  refetchKeypointStats: () => void;
  groupedViewLabels: DicomGroup[];
  searchParams: URLSearchParams;
  hasAnyFilter: boolean;
  setShouldFetchViewLabels: (shouldFetch: boolean) => void;
  setPage: (page: number) => void;
  setSize: (size: number) => void;
  allViews: View[];
  localDecisions: Record<string, Decision | undefined>;
  viewOptions: AdvancedFilterValue[];
  onCreated: (created: any) => void;
  writePageToURL: (next: number, nextSize?: number) => void;
}

interface ViewLabelsState {
  acceptIds: string[];
  isAcceptAllDisabled: boolean;
  advancedFiltersOptions: AdvancedFiltersOptions;
  visibleViewLabels: Map<string, Set<string>>;
  rejectModalOpen: boolean;
  rejectDicomId: string | null;
  rejectDicomViewLabels: ViewLabel[];
  hasAutoAppliedFromQueryParams: boolean;
  isPaginationLoading: boolean;
}

class ViewLabelsClass extends Component<ViewLabelsProps, ViewLabelsState> {
  // handle fallback timeout correctly in a class component
  private paginationTimeout?: ReturnType<typeof setTimeout>;

  constructor(props: ViewLabelsProps) {
    super(props);
    this.state = {
      acceptIds: [],
      isAcceptAllDisabled: false,
      advancedFiltersOptions: {
        datasources: [
          { uuid: 'ABBOTT', name: 'ABBOTT' },
          { uuid: 'ARKHEART', name: 'ARKHEART' },
          { uuid: 'CONWAY', name: 'CONWAY' },
          { uuid: 'DP', name: 'DP' },
          { uuid: 'FSV', name: 'FSV' },
        ],
        dicomICID: '',
        study: '',
        viewLabelICID: '',
        technician: undefined,
        created_at_after: undefined,
        created_at_before: undefined,
        updated_at_after: undefined,
        updated_at_before: undefined,
      },
      visibleViewLabels: new Map(),
      rejectModalOpen: false,
      rejectDicomId: null,
      rejectDicomViewLabels: [],
      hasAutoAppliedFromQueryParams: false,
      isPaginationLoading: false,
    };
  }

  componentDidUpdate(prevProps: ViewLabelsProps) {
    const { searchParams, filterState, hasAnyFilter, setShouldFetchViewLabels } = this.props as any;
    const { hasAutoAppliedFromQueryParams } = this.state;

    // Auto-apply filters from query params on initial load
    if (!hasAutoAppliedFromQueryParams && searchParams && !prevProps.searchParams.toString()) {
      const hasAnyQueryParam = [
        'view',
        'datasources',
        'dicomICID',
        'study',
        'viewLabelICID',
        'technician',
        'created_at_after',
        'created_at_before',
        'updated_at_after',
        'updated_at_before',
      ].some((param: string) => searchParams.get(param));

      const isFilterStateSyncedWithQuery = (fs: any) => {
        const viewParam = searchParams.get('view');
        if (viewParam) {
          const matchesView = fs.view && (fs.view.name === viewParam || fs.view.uuid === viewParam);
          if (!matchesView) return false;
        }

        const singleStringParams = [
          'dicomICID',
          'study',
          'viewLabelICID',
          'created_at_after',
          'created_at_before',
          'updated_at_after',
          'updated_at_before',
        ] as const;

        for (const key of singleStringParams) {
          const qp = searchParams.get(key);
          if (qp) {
            if ((fs.advancedFilters as any)[key] !== qp) return false;
          }
        }

        const datasourceParam = searchParams.get('datasources');
        if (datasourceParam) {
          const ds = fs.advancedFilters?.datasources;
          if (!ds || (ds.name !== datasourceParam && ds.uuid !== datasourceParam)) return false;
        }

        const technicianParam = searchParams.get('technician');
        if (technicianParam) {
          const currentTechnician = fs.advancedFilters?.technician;

          if (!currentTechnician || currentTechnician.uuid !== technicianParam) {
            return false;
          }
        }

        return true;
      };

      const synced = isFilterStateSyncedWithQuery(filterState);

      if (hasAnyQueryParam && hasAnyFilter && synced) {
        setShouldFetchViewLabels(true);
        this.setState({ hasAutoAppliedFromQueryParams: true });
      } else if (hasAnyQueryParam && !hasAnyFilter && !synced) {
        this.setState({ hasAutoAppliedFromQueryParams: true });
      }
    }

    // Reset pagination loading when new data arrives
    if (this.props.viewLabelsPage !== prevProps.viewLabelsPage && this.state.isPaginationLoading) {
      this.setState({ isPaginationLoading: false });
    }

    // Fallback timeout to reset pagination loading if API call takes too long
    if (this.state.isPaginationLoading && !filterState.shouldFetchViewLabels) {
      if (this.paginationTimeout) clearTimeout(this.paginationTimeout);
      this.paginationTimeout = setTimeout(() => {
        this.setState({ isPaginationLoading: false });
      }, 10000); // 10 second timeout
    }

    // Update visible view labels when new view labels arrive (grouped by dicomId)
    if (
      this.props.groupedViewLabels !== prevProps.groupedViewLabels &&
      (this.props.groupedViewLabels?.length ?? 0) > 0
    ) {
      const initialVisibility = new Map<string, Set<string>>();
      this.props.groupedViewLabels.forEach((dicomGroup: DicomGroup) => {
        const dicomId = dicomGroup.uuid;
        const viewLabelIds = new Set<string>(
          (dicomGroup.viewLabels ?? []).map((vl: ViewLabel) => vl.icid)
        );
        initialVisibility.set(dicomId, viewLabelIds);
      });
      this.setState({ visibleViewLabels: initialVisibility });
    }
  }

  componentWillUnmount() {
    if (this.paginationTimeout) clearTimeout(this.paginationTimeout);
  }

  setDecisionsFor = (ids: string[], decision: Decision) => {
    // Use the onLocalDecision prop from the functional component
    ids.forEach(id => {
      this.props.onLocalDecision(id, decision);
    });
  };

  onLocalDecision = (uuid: string, decision: Decision) => this.setDecisionsFor([uuid], decision);

  onSelectView = (_value: AdvancedFilterValue) => {
    // No-op for feature options; view still affects frames query
  };

  onHandleClear = () => {
    this.props.setShouldFetchViewLabels(false);
    this.props.setPage(1);
    this.props.writePageToURL(1, this.props.filterState.size);
    this.setState({
      isPaginationLoading: false,
    });
  };

  clearViewLabels = () => {
    this.onHandleClear();
  };

  onHandleApply = () => {
    this.props.setShouldFetchViewLabels(true);
    this.props.setPage(1);
    this.props.writePageToURL(1, this.props.filterState.size);
    this.setState({
      isPaginationLoading: false,
    });
  };

  onPaginationChange = (_page: number, pageSize: number) => {
    if (!this.props.filterState.shouldFetchViewLabels) return;
    this.props.setPage(_page);
    this.props.setSize(pageSize);
    this.props.writePageToURL(_page, pageSize);
    this.setState({ isPaginationLoading: true });
  };

  onShowSizeChange = (_current: number, newSize: number) => {
    if (!this.props.filterState.shouldFetchViewLabels) return;
    this.props.setPage(1);
    this.props.setSize(newSize);
    this.props.writePageToURL(1, newSize);
    this.setState({ isPaginationLoading: true });
  };

  // accept-all removed

  handleRejectModalOk = async (rejectedIds: string[], comment: string) => {
    const { rejectDicomViewLabels } = this.state;
    const { createReview } = this.props;

    if (!rejectDicomViewLabels) return;
    try {
      // Only submit reviews for explicitly selected (rejected) view labels.
      const reviewsToCreate = rejectDicomViewLabels
        .filter((vl: ViewLabel) => rejectedIds.includes(vl.uuid))
        .map((vl: ViewLabel) => ({
          object_uuid: vl.uuid,
          object_icid: vl.icid,
          object_type: 'VIEW_LABEL',
          object_view: 'default',
          object_view_id: 0,
          decision: Decision.REJECTED,
          comment,
          annotation_link: '',
        }));

      if (reviewsToCreate.length === 0) {
        message.info('No view labels selected for rejection.');
      } else {
        await createReview(reviewsToCreate).unwrap();
        message.success('Selected view labels have been rejected.');

        // Update only the rejected local decisions; leave others untouched.
        rejectDicomViewLabels.forEach(vl => {
          if (rejectedIds.includes(vl.uuid)) {
            this.props.onLocalDecision(vl.uuid, Decision.REJECTED);
          }
        });
      }
    } catch (error) {
      message.error('Failed to submit review');
    }
    this.setState({ rejectModalOpen: false, rejectDicomId: null, rejectDicomViewLabels: [] });
  };

  handleRejectModalCancel = () => {
    this.setState({ rejectModalOpen: false, rejectDicomId: null, rejectDicomViewLabels: [] });
  };

  handleDicomAccept = async (dicomId: string, viewLabels: ViewLabel[]) => {
    const { createReview } = this.props;

    const reviewsToCreate = (viewLabels ?? []).map((vl: ViewLabel) => ({
      object_uuid: vl.uuid,
      object_icid: vl.icid,
      object_type: 'VIEW_LABEL',
      object_view: 'default',
      object_view_id: 0,
      decision: Decision.ACCEPTED,
      comment: '',
      annotation_link: '',
    }));
    await createReview(reviewsToCreate).unwrap();
    message.success(`All view labels for DICOM ${dicomId} have been accepted.`);

    // Re-added: Use the onLocalDecision prop from the functional component
    (viewLabels ?? []).forEach(vl => {
      this.props.onLocalDecision(vl.uuid, Decision.ACCEPTED);
    });
  };

  handleDicomReject = async (dicomId: string, viewLabels: ViewLabel[]) => {
    this.setState({
      rejectModalOpen: true,
      rejectDicomId: dicomId,
      rejectDicomViewLabels: viewLabels,
    });
  };

  handleViewLabelToggle = (dicomId: string, viewLabelId: string, visible: boolean) => {
    this.setState(prevState => {
      const newMap = new Map(prevState.visibleViewLabels);
      const dicomVisibleSet = new Set<string>(newMap.get(dicomId) || []);

      if (visible) {
        dicomVisibleSet.add(viewLabelId);
      } else {
        dicomVisibleSet.delete(viewLabelId);
      }

      newMap.set(dicomId, dicomVisibleSet);
      return { visibleViewLabels: newMap };
    });
  };

  render() {
    const {
      advancedFiltersOptions,
      // review modal removed
      rejectModalOpen,
      rejectDicomViewLabels,
      isPaginationLoading,
      visibleViewLabels,
    } = this.state;

    const {
      filterState,
      getLocalDecision,
      viewLabelsPage,
      isLoadingGetViewLabels,
      keypointStats,
      groupedViewLabels = [],
      hasAnyFilter,
      allViews,
      localDecisions,
      viewOptions,
      onCreated,
    } = this.props as any;

    const viewLabelPageInfo = {
      items: viewLabelsPage?.results || [],
      pages: Math.ceil((viewLabelsPage?.count || 0) / filterState.size),
      size: filterState.size,
      total: viewLabelsPage?.count || 0,
    };

    const selectedStats = (() => {
      if (filterState.shouldFetchViewLabels && viewLabelsPage?.stats) {
        const s = viewLabelsPage.stats;
        return {
          total: s.total_found ?? s.totalFound ?? 0,
          accepted: s.accepted ?? 0,
          rejected: s.rejected ?? 0,
          toBeReviewed: s.to_be_reviewed ?? s.toBeReviewed ?? 0,
        };
      }
      if (keypointStats) {
        const s: any = keypointStats;
        return {
          total: s.totalFound ?? s.total_found ?? 0,
          accepted: s.accepted ?? 0,
          rejected: s.rejected ?? 0,
          toBeReviewed: s.toBeReviewed ?? s.to_be_reviewed ?? 0,
        };
      }
      return null;
    })();

    return (
      <ViewLabelsWrapper>
        <ViewLabelFilter
          primaryOptions={viewOptions}
          advancedFiltersOptions={advancedFiltersOptions}
          handlePrimaryOptChange={this.onSelectView}
          onHandleClear={this.onHandleClear}
          onHandleApply={this.onHandleApply}
          clearViewLabels={this.clearViewLabels}
        />
        {selectedStats && (
          <StatsBarWrapper>
            <StatsContentWrapper>
              <Typography.Text style={{ fontSize: '16px', fontWeight: 'bold' }}>
                {selectedStats.total} Total
              </Typography.Text>
              <Typography.Text type="success" style={{ fontSize: '16px', fontWeight: 'bold' }}>
                {selectedStats.accepted} Accepted
              </Typography.Text>
              <Typography.Text type="danger" style={{ fontSize: '16px', fontWeight: 'bold' }}>
                {selectedStats.rejected} Rejected
              </Typography.Text>
              <Typography.Text type="warning" style={{ fontSize: '16px', fontWeight: 'bold' }}>
                {selectedStats.toBeReviewed} To-be-reviewed
              </Typography.Text>
            </StatsContentWrapper>
            {/* Accept All removed */}
          </StatsBarWrapper>
        )}
        {!filterState.shouldFetchViewLabels ? (
          hasAnyFilter ? (
            <Skeleton />
          ) : (
            <Empty
              description={
                <Typography.Text type="secondary">
                  Please apply filters to view labels.
                </Typography.Text>
              }
            />
          )
        ) : isLoadingGetViewLabels || isPaginationLoading ? (
          <Skeleton />
        ) : (groupedViewLabels || []).length > 0 ? (
          <Row gutter={[24, 24]}>
            {(groupedViewLabels || []).map((dicomGroup: DicomGroup) => {
              const dicomId = dicomGroup.uuid;
              const viewLabels = dicomGroup.viewLabels ?? [];
              const dicomVisibleViewLabels = visibleViewLabels.get(dicomId) || new Set<string>();
              const allInDicomAccepted = (viewLabels ?? []).every((vl: ViewLabel) => {
                const local = getLocalDecision(vl.uuid);
                return local === Decision.ACCEPTED || vl.isApproved;
              });

              return (
                <Col key={dicomId} xs={24} sm={24} md={12} lg={8} xl={8}>
                  <ViewLabelItem
                    dicomGroup={dicomGroup}
                    onViewLabelToggle={(viewLabelId: string, visible: boolean) =>
                      this.handleViewLabelToggle(dicomId, viewLabelId, visible)
                    }
                    visibleViewLabels={dicomVisibleViewLabels}
                    onDicomAccept={this.handleDicomAccept}
                    onDicomReject={this.handleDicomReject}
                    dicomReviewed={allInDicomAccepted}
                    onLocalDecision={this.onLocalDecision}
                    getLocalDecision={getLocalDecision}
                    isRejected={(viewLabels ?? []).some((vl: ViewLabel) => {
                      const local = getLocalDecision(vl.uuid);
                      return local === Decision.REJECTED || vl.isRejected;
                    })}
                    allViews={allViews} // Pass allViews to ViewLabelItem
                    localDecisions={localDecisions}
                    onCreated={onCreated}
                  />
                </Col>
              );
            })}
          </Row>
        ) : (
          <Empty
            description={
              <Typography.Text type="secondary">
                No view labels found for the selected filters.
              </Typography.Text>
            }
          />
        )}
        {filterState.shouldFetchViewLabels && (groupedViewLabels || []).length > 0 && (
          <PaginationWrapper>
            <Pagination
              total={viewLabelPageInfo.total}
              defaultPageSize={filterState.size}
              current={filterState.page}
              onShowSizeChange={this.onShowSizeChange}
              onChange={this.onPaginationChange}
              showTotal={total => `Total ${total} items`}
            />
          </PaginationWrapper>
        )}
        <RejectModal
          isModalOpen={rejectModalOpen}
          handleCancel={this.handleRejectModalCancel}
          handleOk={this.handleRejectModalOk}
          // This prop name depends on your RejectModal interface.
          // Keeping your original name:
          segmentations={rejectDicomViewLabels}
          initialRejectedIds={(rejectDicomViewLabels ?? [])
            .filter((vl: ViewLabel) => {
              const local = getLocalDecision(vl.uuid);
              return (local === Decision.REJECTED || vl.isRejected) && local !== Decision.ACCEPTED;
            })
            .map(vl => vl.uuid)}
        />
        {/* ReviewModal removed */}
      </ViewLabelsWrapper>
    );
  }
}

const ViewLabels = () => {
  const { filterState, updateFilterTags, setShouldFetchViewLabels, setPage, setSize } =
    useViewLabelFilterState();
  const [searchParams, setSearchParams] = useSearchParams();
  const [hasAutoAppliedFromQueryParams, setHasAutoAppliedFromQueryParams] = useState(false);

  // Read initial page from URL (?page=...), default to 1
  useEffect(() => {
    const qp = searchParams.get('page');
    const next = qp ? parseInt(qp, 10) : 1;
    if (Number.isFinite(next) && next > 0 && next !== filterState.page) {
      setPage(next);
    }
  }, [searchParams, filterState.page, setPage]);

  // If ?page is present, auto-enable fetching (even before Apply)
  useEffect(() => {
    const hasPage = !!searchParams.get('page');
    if (hasPage && !filterState.shouldFetchViewLabels) {
      setShouldFetchViewLabels(true);
    }
  }, [searchParams, filterState.shouldFetchViewLabels, setShouldFetchViewLabels]);

  // Helper to set page in URL (always include ?page=...)
  // Merge with window.location.search to avoid overwriting recently-applied filters
  const writePageToURL = (next: number, nextSize?: number) => {
    const params = new URLSearchParams(window.location.search);
    params.set('page', String(next));
    const sizeToWrite = typeof nextSize === 'number' ? nextSize : filterState.size;
    params.set('items_per_page', String(sizeToWrite));
    setSearchParams(params, { replace: true });
  };

  // Sync initial size from URL (?items_per_page=...); default to current `filterState.size` if invalid/missing
  useEffect(() => {
    const qp = searchParams.get('items_per_page');
    const next = qp ? parseInt(qp, 10) : undefined;
    if (next && Number.isFinite(next) && next > 0 && next !== filterState.size) {
      setSize(next);
    }
  }, [searchParams, setSize, filterState.size]);

  // Hook to create a view label (listens for createViewLabel custom events dispatched by child filter)
  const [createViewLabel] = useCreateViewLabelMutation();
  useEffect(() => {
    const handler = async (e: any) => {
      try {
        const payload = e?.detail;
        if (!payload) return;
        await createViewLabel(payload).unwrap();
        message.success('View label created');
      } catch (err) {
        message.error('Failed to create view label');
      }
    };
    window.addEventListener('createViewLabel', handler as any);
    return () => window.removeEventListener('createViewLabel', handler as any);
  }, [createViewLabel]);

  const queryParams = useMemo(
    () => ({
      page: filterState.page,
      size: filterState.size,
      view: filterState.view,
      advancedFilters: {
        datasources: filterState.advancedFilters.datasources,
        dicomICID: filterState.advancedFilters.dicomICID,
        study: filterState.advancedFilters.study,
        annotationSetName: filterState.advancedFilters.annotationSetName,
        viewLabelICID: filterState.advancedFilters.viewLabelICID,
        technician: filterState.advancedFilters.technician,
        created_at_after: filterState.advancedFilters.created_at_after,
        created_at_before: filterState.advancedFilters.created_at_before,
        updated_at_after: filterState.advancedFilters.updated_at_after,
        updated_at_before: filterState.advancedFilters.updated_at_before,
      },
    }),
    [filterState]
  );

  const hasAnyFilter =
    useMemo(
      () =>
        filterState.view ||
        Object.values(filterState.advancedFilters).some(
          val =>
            (Array.isArray(val) && val.length > 0) ||
            (typeof val === 'object' && val !== null && 'name' in val) ||
            (typeof val === 'string' && val.trim() !== '')
        ),
      [filterState]
    ) || filterState.shouldFetchViewLabels;

  const hasAnyQueryParam = useMemo(
    () =>
      [
        'view',
        'datasources',
        'dicomICID',
        'study',
        'viewLabelICID',
        'technician',
        'created_at_after',
        'created_at_before',
        'updated_at_after',
        'updated_at_before',
      ].some(param => searchParams.get(param)),
    [searchParams]
  );

  const isFilterStateSyncedWithQuery = useCallback(
    (fs: any) => {
      const viewParam = searchParams.get('view');
      if (viewParam) {
        const matchesView = fs.view && (fs.view.name === viewParam || fs.view.uuid === viewParam);
        if (!matchesView) return false;
      }

      const singleStringParams = [
        'dicomICID',
        'study',
        'viewLabelICID',
        'created_at_after',
        'created_at_before',
        'updated_at_after',
        'updated_at_before',
      ] as const;

      for (const key of singleStringParams) {
        const qp = searchParams.get(key);
        if (qp) {
          if ((fs.advancedFilters as any)[key] !== qp) return false;
        }
      }

      const datasourceParam = searchParams.get('datasources');
      if (datasourceParam) {
        const ds = fs.advancedFilters?.datasources;
        if (!ds || (ds.name !== datasourceParam && ds.uuid !== datasourceParam)) return false;
      }

      const technicianParam = searchParams.get('technician');
      if (technicianParam) {
        const currentTechnician = fs.advancedFilters?.technician;

        if (!currentTechnician || currentTechnician.uuid !== technicianParam) {
          return false;
        }
      }

      return true;
    },
    [searchParams]
  );

  const { data: views, isLoading: _isLoadingViews } = useGetViewsQuery('');
  const [localViews, setLocalViews] = useState<View[]>(views || []);
  useEffect(() => {
    setLocalViews(views || []);
  }, [views]);
  const [createReview] = useCreateReviewMutation();
  const { data: keypointStats, refetch: refetchKeypointStats } = useGetViewLabelStatsQuery(
    undefined,
    {
      skip: !filterState.shouldFetchViewLabels && hasAnyFilter,
    }
  );

  const viewOptions: AdvancedFilterValue[] = useMemo(() => {
    if (localViews && Array.isArray(localViews) && localViews.length > 0) {
      const sortedViews = [...localViews].sort((a, b) => a.view.localeCompare(b.view));
      return sortedViews.map((view: View) => ({
        uuid: view.uuid,
        name: view.view,
      }));
    }
    return [];
  }, [localViews]);

  // Feature dropdown removed; no featureOptions

  const [localDecisions, setLocalDecisions] = useState<Record<string, Decision | undefined>>({});
  // Store optimistic created view labels keyed by dicom id so newly created labels appear immediately
  const [optimisticCreatedByDicom, setOptimisticCreatedByDicom] = useState<Record<string, any[]>>(
    {}
  );

  const getLocalDecision = useMemo(() => (uuid: string) => localDecisions[uuid], [localDecisions]);
  const onLocalDecision = useMemo(
    () => (uuid: string, decision: Decision) => {
      setLocalDecisions(prev => {
        const next = { ...prev };
        next[uuid] = decision;
        return next;
      });
    },
    []
  );

  const {
    data: viewLabelsPage,
    isLoading: isLoadingGetViewLabels,
    refetch: refetchViewLabels,
  } = useGetViewLabelFramesQuery(queryParams, {
    skip: !filterState.shouldFetchViewLabels,
  });
  const [triggerGetDicom] = useLazyGetViewLabelFramesQuery();

  // Previously we listened for a global 'viewLabelCreated' event and triggered
  // extra refetches. That produced unnecessary API calls. Instead we accept an
  // `onCreated` callback from child items (passed below) that updates optimistic
  // state locally without calling other APIs.

  // Sync local decisions with fetched data (if any)
  useEffect(() => {
    if (viewLabelsPage?.results && viewLabelsPage.results.length > 0) {
      const initialDecisions: Record<string, Decision | undefined> = {};
      viewLabelsPage.results.forEach((dicomGroup: DicomGroup) => {
        (dicomGroup.viewLabels ?? []).forEach((vl: ViewLabel) => {
          if (vl.isApproved) initialDecisions[vl.uuid] = Decision.ACCEPTED;
          else if (vl.isRejected) initialDecisions[vl.uuid] = Decision.REJECTED;
        });
      });
      setLocalDecisions(initialDecisions);
    }
  }, [viewLabelsPage]);

  // Auto-apply filters and fetch frames if query params are present on initial load
  useEffect(() => {
    if (!hasAutoAppliedFromQueryParams) {
      const hydrated = hasAnyFilter; // Assuming hasAnyFilter checks if filterState is populated from URL
      const synced = isFilterStateSyncedWithQuery(filterState);
      if (hasAnyQueryParam && hydrated && synced) {
        setShouldFetchViewLabels(true);
        setHasAutoAppliedFromQueryParams(true);
      }
    }
  }, [
    searchParams,
    filterState,
    hasAutoAppliedFromQueryParams,
    hasAnyQueryParam,
    isFilterStateSyncedWithQuery,
    setShouldFetchViewLabels,
    hasAnyFilter,
  ]);

  // Trigger fetch when flag flips on
  useEffect(() => {
    if (filterState.shouldFetchViewLabels) {
      refetchViewLabels();
    }
  }, [filterState.shouldFetchViewLabels, refetchViewLabels]);

  // Keep filter tags updated
  useEffect(() => {
    updateFilterTags();
  }, [filterState, updateFilterTags]);

  const mergedGroupedViewLabels: DicomGroup[] = useMemo(() => {
    const results = viewLabelsPage?.results || [];
    return results.map((dg: DicomGroup) => {
      const replacement = optimisticCreatedByDicom[dg.uuid];
      if (!replacement || replacement.length === 0) return dg;
      // If we have an optimistic replacement array for this dicom, use it as the
      // authoritative list for display (replace), not append to the server list.
      return {
        ...dg,
        viewLabels: replacement,
      } as DicomGroup;
    });
  }, [viewLabelsPage, optimisticCreatedByDicom]);

  return (
    <ViewLabelsClass
      filterState={filterState}
      onLocalDecision={onLocalDecision}
      getLocalDecision={getLocalDecision}
      viewLabelsPage={viewLabelsPage}
      isLoadingGetViewLabels={isLoadingGetViewLabels}
      views={views}
      kpClasses={views} // pass views as kpClasses for now if that's intentional
      createReview={createReview}
      keypointStats={keypointStats}
      refetchKeypointStats={refetchKeypointStats}
      groupedViewLabels={mergedGroupedViewLabels}
      searchParams={useMemo(() => new URLSearchParams(searchParams), [searchParams])}
      hasAnyFilter={hasAnyFilter}
      setShouldFetchViewLabels={setShouldFetchViewLabels}
      setPage={setPage}
      setSize={setSize}
      allViews={localViews} // Pass allViews to ViewLabelItem
      localDecisions={localDecisions}
      viewOptions={viewOptions}
      onCreated={async (created: any) => {
        // Add created view label into per-dicom optimistic listing.
        const dicomId = created?.dicom || created?.dicomId || created?.dicom_uuid;
        if (dicomId) {
          // Replace any previous optimistic-created view label for this dicom
          setOptimisticCreatedByDicom(prev => ({ ...prev, [dicomId]: [created] }));
        }

        // If the created response contains a viewClass, append it to localViews
        // so the view listing (and filter dropdown) includes it without refetching.
        const vc = created?.viewClass;
        if (vc && vc.uuid) {
          setLocalViews(prev => {
            if (prev.some(v => v.uuid === vc.uuid)) return prev;
            return [...prev, vc];
          });
        }
        // Fetch authoritative view-label-dicom for this dicom and replace optimistic
        // entry so any previous view labels are replaced by the server's response.
        try {
          const params = {
            page: 1,
            size: 10,
            view: undefined,
            advancedFilters: { dicomICID: created?.dicom },
          } as any;
          const fetched = await triggerGetDicom(params).unwrap();
          const fetchedGroup = fetched?.results?.[0];
          if (fetchedGroup && fetchedGroup.uuid) {
            // First, create reviews to accept the new label and reject the others on the server
            try {
              const reviewsToCreate = (fetchedGroup.viewLabels || []).map((vl: any) => ({
                object_uuid: vl.uuid,
                object_icid: vl.icid,
                object_type: 'VIEW_LABEL',
                object_view: 'default',
                object_view_id: 0,
                decision: vl.uuid === created?.uuid ? Decision.ACCEPTED : Decision.REJECTED,
                comment: '',
                annotation_link: '',
              }));
              if (reviewsToCreate.length > 0) {
                await createReview(reviewsToCreate).unwrap();
              }
            } catch (err) {
              // ignore review errors but continue to update UI
            }

            // Use authoritative viewLabels for this dicom to replace optimistic ones
            setOptimisticCreatedByDicom(prev => ({
              ...prev,
              [fetchedGroup.uuid]: fetchedGroup.viewLabels || [],
            }));
          }
        } catch (err) {
          // ignore failed fetch; keep optimistic state
        }
      }}
      writePageToURL={writePageToURL}
    />
  );
};

export default ViewLabels;
